import graphqlService from "@/services/graphql.service";
import { uploadFileWithToken } from "@/services/uploadMedia.service";
import { GRAPHQL_PATH, GRAPHQL_PUBLIC_PATH } from "@/services/config";
import {
    quizzesQueryTypeMap,
    quizCollaboratorsQueryTypeMap,
    updateQuizTypeMap,
    updateQuizOptionsTypeMap,
    quizIdMutationTypeMap,
    shareQuizTypeMap,
    quizAccessTypeMap,
} from "@/graphql/type_maps/type.maps";
import {
    fragmentUpdateQuizBase,
    fragmentUpdateQuizDomain,
    fragmentUpdateQuizEmbedDomains,
    fragmentUpdateQuizOptionsDocs,
    fragmentUpdateQuizOptionsQrCode,
    fragmentQuizCollaboratorBase,
    fragmentCopyQuizBase,
} from "@/graphql/fragments/mutation/quizzes.fragments";
import { fragmentQuizzesPaginationBase } from "@/graphql/fragments/query/quizzes.fragments";
import { fragmentQuizCollaboratorsPagination } from "@/graphql/fragments/query/quizCollaborators.fragments";

async function getQuizzesPagination(params = {}) {
    const { fragment = fragmentQuizzesPaginationBase, variables, path } = params;

    const data = await graphqlService.requestBuiltQuery({
        fragment,
        field: "quizzes",
        variables,
        typeMap: quizzesQueryTypeMap,
        path,
    });

    return data?.quizzes;
}

async function getQuizzesData(params = {}) {
    const pagination = await getQuizzesPagination(params);
    return Array.isArray(pagination?.data) ? pagination.data : undefined;
}

async function getQuizData(params = {}) {
    const quizzes = await getQuizzesData(params);
    return Array.isArray(quizzes) ? quizzes[0] : undefined;
}

async function updateQuiz(variables = {}, fragment = fragmentUpdateQuizBase, path) {
    if (!variables?.id) throw new Error("updateQuiz: id is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "UpdateQuiz",
        field: "updateQuiz",
        variables,
        typeMap: updateQuizTypeMap,
        path,
    });

    return data?.updateQuiz;
}

async function updateQuizDomain({ id, domain, project_id }, fragment = fragmentUpdateQuizDomain, path) {
    return updateQuiz({ id, domain, project_id }, fragment, path);
}

async function updateQuizEmbedDomains({ id, embed_domains }, fragment = fragmentUpdateQuizEmbedDomains, path) {
    const quiz = await updateQuiz({ id, embed_domains }, fragment, path);
    return quiz?.embed_domains;
}

async function registerQuizEmbedDomain({ code, domain, baseBackendUrl, path } = {}) {
    if (!code) throw new Error("registerQuizEmbedDomain: code is required");
    if (!domain) throw new Error("registerQuizEmbedDomain: domain is required");

    const query = /* GraphQL */ `
        mutation RegisterEmbedDomain($code: String!, $domain: String!) {
            registerQuizEmbedDomain(code: $code, domain: $domain) {
                id
                code
                embed_domains
            }
        }
    `;

    const data = await graphqlService.requestGraphql({
        query,
        variables: { code, domain },
        path: path || (baseBackendUrl ? `${baseBackendUrl}/graphql/public` : GRAPHQL_PUBLIC_PATH),
    });

    return data?.registerQuizEmbedDomain;
}

async function updateQuizOptions(variables = {}, fragment = fragmentUpdateQuizOptionsDocs, path) {
    if (!variables?.id) throw new Error("updateQuizOptions: id is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "UpdateQuizOptions",
        field: "updateQuizOptions",
        variables,
        typeMap: updateQuizOptionsTypeMap,
        path,
    });

    return data?.updateQuizOptions;
}

async function updateQuizDocs({ id, custom_privacy_policy_url }, fragment = fragmentUpdateQuizOptionsDocs, path) {
    return updateQuizOptions({ id, custom_privacy_policy_url }, fragment, path);
}

async function updateQuizQrCode({ id, qr_code }, fragment = fragmentUpdateQuizOptionsQrCode, path) {
    return updateQuizOptions({ id, qr_code }, fragment, path);
}

async function updateQuizAndOptions({ quizVariables = {}, optionsVariables = {}, quizFragment = fragmentUpdateQuizBase, optionsFragment, path } = {}) {
    if (!quizVariables?.id) throw new Error("updateQuizAndOptions: quizVariables.id is required");
    if (!optionsVariables?.id) throw new Error("updateQuizAndOptions: optionsVariables.id is required");

    const qVars = Object.fromEntries(Object.entries(quizVariables).map(([key, value]) => [`quiz_${key}`, value]));
    const oVars = Object.fromEntries(Object.entries(optionsVariables).map(([key, value]) => [`options_${key}`, value]));
    const variables = { ...qVars, ...oVars };
    const quizTypeMap = Object.fromEntries(Object.entries(updateQuizTypeMap).map(([key, value]) => [`quiz_${key}`, value]));
    const optionsTypeMap = Object.fromEntries(Object.entries(updateQuizOptionsTypeMap).map(([key, value]) => [`options_${key}`, value]));

    const quizArgs = Object.keys(quizVariables).map((key) => `${key}: $quiz_${key}`).join(", ");
    const optionsArgs = Object.keys(optionsVariables).map((key) => `${key}: $options_${key}`).join(", ");
    const typeMap = { ...quizTypeMap, ...optionsTypeMap };
    const varDefs = Object.entries(variables)
        .map(([key]) => `$${key}: ${typeMap[key] || "String"}`)
        .join(", ");

    const quizSelection = quizFragment
        ? `...${/fragment\s+(\w+)\s+on\s+\w+/i.exec(quizFragment)?.[1]}`
        : "id";
    const optionsSelection = optionsFragment
        ? `...${/fragment\s+(\w+)\s+on\s+\w+/i.exec(optionsFragment)?.[1]}`
        : "id";

    const query = /* GraphQL */ `
        ${quizFragment || ""}
        ${optionsFragment || ""}
        mutation UpdateQuizAndOptions(${varDefs}) {
          updateQuiz(${quizArgs}) {
            ${quizSelection}
          }
          updateQuizOptions(${optionsArgs}) {
            ${optionsSelection}
          }
        }
    `;

    const data = await graphqlService.requestGraphql({ query, variables, path });
    return {
        updateQuiz: data?.updateQuiz,
        updateQuizOptions: data?.updateQuizOptions,
    };
}

async function copyQuiz(id, fragment = fragmentCopyQuizBase, path) {
    if (!id) throw new Error("copyQuiz: id is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "CopyQuiz",
        field: "copyQuiz",
        variables: { id },
        typeMap: quizIdMutationTypeMap,
        path,
    });

    return data?.copyQuiz;
}

async function restoreQuiz(id, path) {
    if (!id) throw new Error("restoreQuiz: id is required");

    const data = await graphqlService.requestBuiltMutation({
        mutationName: "RestoreQuiz",
        field: "restoreQuiz",
        variables: { id },
        typeMap: quizIdMutationTypeMap,
        path,
    });

    return data?.restoreQuiz ?? false;
}

async function deleteQuiz(id, path) {
    if (!id) throw new Error("deleteQuiz: id is required");

    const data = await graphqlService.requestBuiltMutation({
        mutationName: "DeleteQuiz",
        field: "deleteQuiz",
        variables: { id },
        typeMap: quizIdMutationTypeMap,
        path,
    });

    return data?.deleteQuiz ?? false;
}

async function getQuizCollaborators(params = {}) {
    const { fragment = fragmentQuizCollaboratorsPagination, variables, path } = params;

    const data = await graphqlService.requestBuiltQuery({
        fragment,
        field: "quizCollaborators",
        variables,
        typeMap: quizCollaboratorsQueryTypeMap,
        path,
    });

    return data?.quizCollaborators;
}

async function shareQuiz(variables = {}, fragment = fragmentQuizCollaboratorBase, path) {
    if (!variables?.quiz_id) throw new Error("shareQuiz: quiz_id is required");
    if (!variables?.user_email && !variables?.email) throw new Error("shareQuiz: user_email or email is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "ShareQuiz",
        field: "shareQuiz",
        variables,
        typeMap: shareQuizTypeMap,
        path,
    });

    return data?.shareQuiz;
}

async function revokeQuizAccess(id, fragment = fragmentQuizCollaboratorBase, path) {
    if (!id) throw new Error("revokeQuizAccess: id is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "RevokeQuizAccess",
        field: "revokeQuizAccess",
        variables: { id },
        typeMap: quizAccessTypeMap,
        path,
    });

    return data?.revokeQuizAccess;
}

async function updateQuizAccessStatus({ id, status }, fragment = fragmentQuizCollaboratorBase, path) {
    if (!id) throw new Error("updateQuizAccessStatus: id is required");

    const data = await graphqlService.requestBuiltMutation({
        fragment,
        mutationName: "UpdateQuizAccessStatus",
        field: "updateQuizAccessStatus",
        variables: { id, status },
        typeMap: quizAccessTypeMap,
        path,
    });

    return data?.updateQuizAccessStatus;
}




async function getLeadsAndQuiz({ quiz_id, leads_limit, page, path }) {
    const query = /* GraphQL */ `
        query LeadsAndQuiz($quizId: ID) {
          leads(quiz_id: "${quiz_id}", limit: ${leads_limit}, page: ${page}) {
            data {
              uuid
              created_at
              sended_at
              answers {
                id
                question { id text answer_type required }
                skipped
                custom
                variant { id answer }
              }
              contacts {
                id
                field { id name placeholder required display }
                value
              }
              utm {
                utm_source
                utm_medium
                utm_campaign
                utm_term
                utm_content
                gclid
                page
              }
              points_total
              points_result {
                title
              }
              ip_address
            }
            total
            per_page
            current_page
            from
            to
            last_page
            has_more_pages
          }

          quizzes(id: $quizId) {
            data {
              questions {
                id
                answer_type
                options {
                  ... on RatingProperties {
                    titles
                    pack_id
                  }
                }
              }
            }
          }
        }
    `;

    return graphqlService.requestGraphql({ query, variables: { quizId: quiz_id }, path });
}

async function updateQuizLogotypeUpload({ id, project_id, logotype, quizCode, path = GRAPHQL_PATH }) {
    if (!id) throw new Error("updateQuizLogotypeUpload: id is required");
    if (!(logotype instanceof File)) throw new Error("updateQuizLogotypeUpload: logotype must be File");

    const { uploadToken } = await uploadFileWithToken({
        file: logotype,
        modelType: "quiz",
        field: "logotype",
        quizCode,
    });

    const projectPart = `project_id: ${project_id ? `"${project_id}"` : null},`;
    const query = /* GraphQL */ `
        mutation UpdateQuizLogotype($logotype: String) {
          updateQuiz(id: "${id}", ${projectPart} logotype: $logotype) {
            id
            logotype {
              id
              hint
              filename
              path
              type
              size
              width
              height
              created_at
              display_settings {
                desktop { background_size horizontal_position vertical_position }
                mobile { background_size horizontal_position vertical_position }
              }
            }
          }
        }
    `;

    const data = await graphqlService.requestGraphql({
        query,
        variables: { logotype: uploadToken },
        path,
    });

    return data?.updateQuiz;
}

/**
 * @param {boolean} [params.pointsUpdateAllowed=true] when false, the `points`
 *   argument is omitted rather than resent — this is a quiz-wide
 *   grandfathered setting derived from result_page visibility (see
 *   AccordionItemResultPage.vue), not something classifiable here.
 * @param {boolean} [params.redirectUpdateAllowed=true] turning auto_redirect
 *   off is never gated, only turning it on.
 */
function buildUpdateQuizOptionsMutationPart({ quizData, project_id, pointsUpdateAllowed = true, redirectUpdateAllowed = true }) {
    const pointsSettings = quizData.options.points || {};
    const projectPart = `project_id: ${project_id ? `"${project_id}"` : null}, `;
    const wantsRedirectOn = !!quizData.options.auto_redirect;
    const autoRedirectArg = (wantsRedirectOn && !redirectUpdateAllowed)
        ? ''
        : `auto_redirect: ${wantsRedirectOn},
                auto_redirect_url: ${wantsRedirectOn ? JSON.stringify(quizData.options.auto_redirect_url) : '""'},`;
    const pointsArg = !pointsUpdateAllowed
        ? ''
        : `points: {
                    enabled: ${!!pointsSettings.enabled},
                    show_each_page: ${!!pointsSettings.show_each_page},
                    formula: ${JSON.stringify(pointsSettings.formula || "")}
                }`;

    return `
            updateQuiz(id:"${quizData.id}", ${projectPart} name:${JSON.stringify(quizData.name)}, contacts:${JSON.stringify(quizData.contacts)}, timezone: ${quizData.timezone}){id name contacts timezone}
            updateQuizOptions(
                id: "${quizData.options.id}",
                show_progress: ${quizData.options.show_progress !== false},
                auto_next: ${quizData.options.auto_next},
                animation: "${quizData.options.animation}",
                ${autoRedirectArg}
                benefits: {
                    start_page: ${quizData.options.benefits.start_page},
                    questions: ${quizData.options.benefits.questions},
                    contact_page: ${quizData.options.benefits.contact_page},
                    finish_page: ${quizData.options.benefits.finish_page},
                }
                ${pointsArg}
            ) {
                id
                show_progress
                auto_next
                auto_redirect
                auto_redirect_url
                animation
                benefits {
                    start_page
                    questions
                    contact_page
                    finish_page
                }
                points {
                    enabled
                    show_each_page
                    formula
                }
            }
    `;
}



/**
 * @param {boolean} [redirectUpdateAllowed=true] turning auto_redirect off is
 *   never gated, only turning it on.
 */
function buildUpdateQuizOptionsAutoRedirectMutationPart({ id, auto_redirect, redirectUpdateAllowed = true }) {
    if (auto_redirect && !redirectUpdateAllowed) {
        return '';
    }

    return `uof : updateQuizOptions(
                    id: "${id}",
                    auto_redirect: ${auto_redirect},
                ) {
                    id
                    auto_redirect
                }`;
}

function buildUpdateQuizOptionsLangMutationPart({ id, lang }) {
    return `updateQuizOptions(
                    id: "${id}",
                    lang: "${lang}",
                ) {
                    id
                    lang
                }`;
}

function buildUpdateQuizDesignOptionsMutationPart({ quizData, customDesign }) {
    const customDesignString = JSON.stringify(customDesign, null, 2);

    return `updateQuizOptions(
                    id: "${quizData.options.id}",
                    show_progress: ${quizData.options.show_progress !== false},
                    theme: "${quizData.options.theme}",
                    corners: "${quizData.options.corners}",
                    custom_design: ${JSON.stringify(customDesignString)}
                ) {
                    id
                    show_progress
                    corners
                    theme
                    custom_design
                }`;
}

export default {
    getQuizzesPagination,
    getQuizzesData,
    getQuizData,
    updateQuiz,
    updateQuizDomain,
    updateQuizEmbedDomains,
    registerQuizEmbedDomain,
    updateQuizOptions,
    updateQuizDocs,
    updateQuizQrCode,
    updateQuizAndOptions,
    updateQuizLogotypeUpload,
    getLeadsAndQuiz,
    buildUpdateQuizOptionsMutationPart,
    buildUpdateQuizDesignOptionsMutationPart,
    buildUpdateQuizOptionsLangMutationPart,
    buildUpdateQuizOptionsAutoRedirectMutationPart,
    copyQuiz,
    restoreQuiz,
    deleteQuiz,
    getQuizCollaborators,
    shareQuiz,
    revokeQuizAccess,
    updateQuizAccessStatus,
};
